import { configureStore, ThunkAction, Action, combineReducers } from '@reduxjs/toolkit';
import LogRocket from 'logrocket';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import { persistReducer, persistStore } from 'redux-persist';
import { authSlice } from './User/AuthSlice';
import { userInfoSlice } from './User/UserInfoSlice';
import { growerSlice } from './Grower/GrowerSlice';
import { uiSlice } from './UI/UISlice';
import { attributesSlice } from './Seeds/AttributesSlice';
import { cropsSlice } from './Seeds/CropsSlice';
import { userSeedAttributeSlice } from './User/UserSeedAttributesSlice';
import { customTreatmentsSlice } from './User/CustomTreatmentsSlice';
import { seedsSlice } from './Seeds/SeedsSlice';
import createMigrate from 'redux-persist/es/createMigrate';
import { configSlice } from './User/ConfigSlice';
import { plantingRateSlice } from './Seeds/PlantingRateSlice';
import { CLUSlice } from './CLUs/CLUSlice';
import { SoilDataSlice } from './SoilData/SoilDataSlice';
import { recordingSlice } from './UI/RecordingSlice';
import { migrations } from './StoreMigrations';
import { managementZoneEditsSlice } from './ManagementZones/ManagementZoneSlice';
import { fileSlice } from './MachineFileData/FileSlice';
import { bulkImportSlice } from './BulkImport/BulkImportSlice';
import { fieldPlanSlice } from './Plans/FieldPlan/FieldPlanSlice';
import { userAdminSlice } from './UserAdmin/UserAdminSlice';
import { appListener } from '../Middleware/AppListener';
import { globalSession } from '../../tracing/session';
import localforage from 'localforage';
import { productGamePlanSliceV7 } from './Plans/ProductGamePlanSliceV7';
import { insightsSlice } from './Insights/InsightsSlice';

localforage.config({
	name: 'ghx-fields',
	storeName: 'ghx-fields-store',
	driver: localforage.INDEXEDDB,
});

const rootPersistConfig = {
	key: 'root',
	storage: localforage,
	version: 15, // New version for IndexedDB migration
	blacklist: [
		'auth',
		'grower',
		'seedAttributes',
		'crops',
		'userAttributes',
		'seeds',
		'productGamePlan',
		'productGamePlanV7',
		'plantingRate',
		'orders',
		'ui',
		'managementZoneEdits',
		'bulkImport',
		'fieldplan',
		'userInfo',
		'userAdmin'
	],
	migrate: createMigrate(migrations)
};

const authPersistConfig = {
	key: 'auth',
	storage: localforage,
	blacklist: ['isLoading']
};

const userInfoPersistConfig = {
	key: 'userInfo',
	storage: localforage,
	blacklist: ['isLoading', 'ShipFromAddresses']
};

const growerPersistConfig = {
	key: 'grower',
	storage: localforage,
	blacklist: [
		'isLoading',
		'isLoadingGrowers',
		'isLoadingEnogenContracts',
		'LoadingGrowerOrders',
		'isLoadingFarms',
		'isLoadingFields',
		'isLoadingDraftQuotes',
		'isLoadingPlans',
		'isLoadingOrderPlanMaxScript',
		'isLoadingBulkCropUpdateMaxScript',
		'isLoadingPlanUpdate',
		'isLoadingPlanDelete',
		'isLoadingPlanLaunch',
		'isLoadingPlanLaunchExisting',
		'isLoadingPlanPdfDownload',
		'isLoadingDiscountMatrix',
		'isLoadingStartBulkImport',
		'isLoadingInvitation',
		'isSavingFarm',
		'isSavingField',
		'isDeletingFarm',
		'isDeletingField',
		'loadingCounter',
		'isLoadingPlantingPlansSummaries',
		'isLoadingGetPlantingPlansSummaries',
		'isLoadingFieldPlantingPlan',
		'isLoadingSaveManagementZone',
		'isLoadingUpdateFieldPlantingPlan',
		'isLoadingDownloadFieldPlantingPlan',
		'showSuccessNotification',
		'ExpectingGrowerDataUserId',
		'cancelableRequestIds',
		'isLoadingCsiPdf',
		'isLoadingPpgPdf',
	]
};

const seedAttrsPersistConfig = {
	key: 'seedAttributes',
	storage: localforage,
	blacklist: ['isLoading']
};

const customTreatmentsPersistConfig = {
	key: 'customTreatments',
	storage: localforage,
	blacklist: ['isLoadingGetTreatments', 'isLoadingSaveTreatments', 'isLoadingGetUserDefaultTreatments', 'isLoadingSaveUserDefaultTreatments']
};

const cropsPersistConfig = {
	key: 'crops',
	storage: localforage,
	blacklist: ['isLoading']
};

const userAttrsPersistConfig = {
	key: 'userAttributes',
	storage: localforage,
	blacklist: [
		'isLoading',
		'isSaving'
	]
};

const seedsPersistConfig = {
	key: 'seeds',
	storage: localforage,
	blacklist: [
		'isLoading',
		'isGettingSeedsForGrower',
		'isGettingSeedsForCurrentUser',
		'growerSeeds',
	],
};

const productGameplanV7PersistConfig = {
	key: 'productGamePlanV7',
	storage: localforage,
	blacklist: [
		'isLoadingPlan',
		'isLoadingRecommendation',
	]
};

const plantingRatePersistConfig = {
	key: 'plantingRate',
	storage: localforage,
	blacklist: ['isLoadingPlantingRates']
};

const cluPersistConfig = {
	key: 'clu',
	storage: localforage,
	blacklist: ['isLoading']
};

const soilDataPersistConfig = {
	key: 'soilData',
	storage: localforage,
	blacklist: ['isLoading']
};

const recordingPersistConfig = {
	key: 'recording',
	storage: localforage,
	blacklist: ['isRecording', 'cardId']
};

const uiPersistConfig = {
	key: 'ui',
	storage: localforage,
	blacklist: ['isLoading', 'isError', 'errorMessage', 'MapInteractionMode', 'SelectedFieldForEdit', 'ShowDrawingTools', 'DrawnFeatures']
};

const managementZoneEditsPersistConfig = {
	key: 'managementZoneEdits',
	storage: localforage,
	blacklist: ['isLoading', 'isError', 'errorMessage', 'undoStack', 'mode', 'selectedZone', 'zoneEditingState']
};

const filePersistConfig = {
	key: 'file',
	storage: localforage,
	blacklist: ['isLoadingFileListing', 'isLoadingFileUrl', 'isError', 'errorMessage', 'FileListing', 'FileUploadList']
};

const bulkImportPersistConfig = {
	key: 'bulkImport',
	storage: localforage,
	blacklist: [
		'errorMessage',
		'farmKey',
		'farms',
		'fieldKey',
		'geometry',
		'header',
		'isError',
		'isLoadingGeometry',
		'isLoadingHeader',
		'isLoadingProfile',
		'isLoadingPublish',
		'isSuccess',
		'sessionInProgress',
	]
};

const fieldPlanPersistConfig = {
	key: 'fieldplan',
	storage: localforage,
	blacklist: [
		'errorMessage',
		'isLoading',
		'isSuccess',
		'isError',
		'pollComplete',
		'pollLoading',
		'assignmentChanges',
		'highlightHybridId',
	]
};

const insightsPersistConfig =
{
	key: 'insights',
	storage: localforage,
	blacklist: [
		'isLoadingExperience',
		'isLoadingAnalyses',
		'isError',
		'errorMessage',
		'aiExperience',
		'currentSalesRepId',
		'currentSellerId',
		'currentGrowerId',
		'multiSalesRepAnalyses',
		'singleSalesRepAnalysis',
		'sellerMemberAnalyses',
		'singleSellerAnalysis',
		'singleGrowerAnalysis',
	]
};

const rootReducer = combineReducers({
	auth: persistReducer(authPersistConfig, authSlice.reducer),
	userInfo: persistReducer(userInfoPersistConfig, userInfoSlice.reducer),
	grower: persistReducer(growerPersistConfig, growerSlice.reducer),
	ui: persistReducer(uiPersistConfig, uiSlice.reducer),
	seedAttributes: persistReducer(seedAttrsPersistConfig, attributesSlice.reducer),
	crops: persistReducer(cropsPersistConfig, cropsSlice.reducer),
	userAttributes: persistReducer(userAttrsPersistConfig, userSeedAttributeSlice.reducer),	
	seeds: persistReducer(seedsPersistConfig, seedsSlice.reducer),
	productGamePlanV7: persistReducer(productGameplanV7PersistConfig, productGamePlanSliceV7.reducer),
	config: configSlice.reducer,
	clu: persistReducer(cluPersistConfig, CLUSlice.reducer),
	plantingRate: persistReducer(plantingRatePersistConfig, plantingRateSlice.reducer),
	customTreatments: persistReducer(customTreatmentsPersistConfig, customTreatmentsSlice.reducer),
	soilData: persistReducer(soilDataPersistConfig, SoilDataSlice.reducer),
	recording: persistReducer(recordingPersistConfig, recordingSlice.reducer),
	managementZoneEdits: persistReducer(managementZoneEditsPersistConfig, managementZoneEditsSlice.reducer),
	file: persistReducer(filePersistConfig, fileSlice.reducer),
	bulkImport: persistReducer(bulkImportPersistConfig, bulkImportSlice.reducer),
	fieldplan: persistReducer(fieldPlanPersistConfig, fieldPlanSlice.reducer),
	userAdmin: userAdminSlice.reducer,
	insights: persistReducer(insightsPersistConfig, insightsSlice.reducer),
});

const persistedReducer = persistReducer(rootPersistConfig, rootReducer);

export const store = configureStore({
	reducer: persistedReducer,
	middleware: (getDefaultMiddleware) =>
	{
		const middleware = getDefaultMiddleware({
			// https://redux-toolkit.js.org/usage/usage-guide#working-with-non-serializable-data
			serializableCheck: false,
			immutableCheck: false,
		});
		middleware.push(LogRocket.reduxMiddleware());
		middleware.push(appListener.middleware);
		return middleware;
	},
});

export const persistor = persistStore(store, {  }, () => 
{
	// After rehydrate, we need to let amplitude know our identity (if we have one)
	const userInfo = store.getState()?.userInfo;
	const accessToken = store.getState()?.auth?.userAuthToken;
	if(accessToken)
	{
		// Apply this access token to global session
		globalSession.initializeSession(accessToken, userInfo.UserId, userInfo.FoundationId, userInfo.UserEmail, userInfo.UserName);
	}
});

export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof rootReducer>;
export type AppThunk<ReturnType = void> = ThunkAction<ReturnType, RootState, unknown, Action<string>>;

// Typed versions of standard redux functions.
// https://redux.js.org/usage/usage-with-typescript#define-typed-hooks
export const useAppDispatch: () => AppDispatch = useDispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;